import { Dashboard } from "@/components/dashboard/Dashboard/Dashboard";
import { VStack } from "@/components/layout/VStack";
import { useMyExplorations } from "../hooks/myExplorations/useMyExplorations";
import { useEffect, useState } from "react";
import { Skeleton } from "@/components/ui/Skeleton/Skeleton";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/Empty/Empty";
import { Button } from "@/components/ui/Button/Button";
import { BookPlus } from "lucide-react";
import { HStack } from "@/components/layout/HStack";
import { MyExplorations } from "../components/myExplorations/MyExplorations";
import { CreateExplorationSheet } from "../components/myExplorations/CreateExplorationSheet";
import { DeleteExplorationDialog } from "../components/myExplorations/DeleteExplorationDialog";
import { EditExplorationSheet } from "../components/myExplorations/EditExplorationSheet";
import { ArchiveToggleButton } from "@/components/elements/ArchiveToggleButton/ArchiveToggleButton";

const TeacherMyExplorations = ({
  archived = false,
}: {
  archived?: boolean;
}) => {
  const [explorations, setExplorations] = useState<{
    explorations: any[];
    classes: any[];
    rotations: any[];
    sessions: any[];
    teachers: any[];
  }>({
    explorations: [],
    classes: [],
    rotations: [],
    sessions: [],
    teachers: [],
  });
  const [open, setOpen] = useState<{
    create: { open: boolean; prefill: any };
    edit: { open: boolean; exploration: any };
    delete: { open: boolean; exploration: any };
  }>({
    create: { open: false, prefill: undefined },
    edit: { open: false, exploration: undefined },
    delete: { open: false, exploration: undefined },
  });

  const { loadMyExplorations, loading } = useMyExplorations({ archived });

  useEffect(() => {
    loadMyExplorations(setExplorations);
  }, [archived]);

  const openDelete = (explorationId: string) => {
    const exploration = explorations.explorations.find(
      (e) => e._id === explorationId,
    );

    setOpen((prev) => ({
      ...prev,
      delete: {
        open: true,
        exploration,
      },
    }));
  };

  const openEdit = (explorationId: string) => {
    const exploration = explorations.explorations.find(
      (e) => e._id === explorationId,
    );
    const rotationId = explorations.sessions.find(
      (s) => s._id === exploration.session,
    ).rotation;

    setOpen((prev) => ({
      ...prev,
      edit: {
        open: true,
        exploration: {
          ...exploration,
          rotation: rotationId,
        },
      },
    }));
  };

  const openDuplicate = (explorationId: string) => {
    const exploration = explorations.explorations.find(
      (e) => e._id === explorationId,
    );
    const rotationId = explorations.sessions.find(
      (s) => s._id === exploration.session,
    ).rotation;

    const copySuffix = " (Copy)";
    const baseName = exploration.name ?? "";
    const name =
      (baseName + copySuffix).length > 120
        ? baseName.slice(0, 120 - copySuffix.length) + copySuffix
        : baseName + copySuffix;

    setOpen((prev) => ({
      ...prev,
      create: {
        open: true,
        prefill: {
          rotation: rotationId,
          session: exploration.session,
          name,
          description: exploration.description,
          gradeLevels: exploration.gradeLevels,
          pathway: exploration.pathway,
          room: exploration.room,
          minEnrollment: exploration.minEnrollment,
          maxEnrollment: exploration.maxEnrollment,
          coTeachers: exploration.coTeachers,
        },
      },
    }));
  };

  return (
    <Dashboard
      activePage={archived ? "/explorations/me/archive" : "/explorations/me"}
    >
      <HStack justify="space-between" wrap>
        <VStack>
          <h2
            style={{
              fontSize: "var(--text-2xl)",
              lineHeight: "var(--line-height-2xl)",
              fontWeight: "var(--font-bold)",
            }}
          >
            {archived ? "Archived My Explorations" : "My Explorations"}
          </h2>
          <p style={{ color: "var(--muted-foreground)" }}>
            {archived
              ? "View archived explorations you have created."
              : "View, edit, and create explorations."}
          </p>
        </VStack>
        <HStack>
          <ArchiveToggleButton />
          {!archived && (
            <Button
              onClick={() =>
                setOpen((prev) => ({
                  ...prev,
                  create: { open: true, prefill: undefined },
                }))
              }
            >
              <BookPlus /> Create Exploration
            </Button>
          )}
        </HStack>
      </HStack>
      {loading.loadMyExplorations ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr 1fr",
            gap: "1rem",
          }}
        >
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} style={{ height: "240px", width: "100%" }} />
          ))}
        </div>
      ) : explorations.explorations.length < 1 ? (
        <Empty style={{ borderColor: "var(--secondary-border)" }}>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <BookPlus />
            </EmptyMedia>
            <EmptyTitle>
              {archived
                ? "No Archived Explorations"
                : "No Explorations Found"}
            </EmptyTitle>
            <EmptyDescription>
              {archived
                ? "There are no archived explorations you have created."
                : "There were no explorations found, if you believe this is an error, please refresh."}
            </EmptyDescription>
          </EmptyHeader>
          {!archived && (
            <EmptyContent>
              <Button
                onClick={() =>
                  setOpen((prev) => ({
                    ...prev,
                    create: { open: true, prefill: undefined },
                  }))
                }
              >
                <BookPlus /> Create Exploration
              </Button>
            </EmptyContent>
          )}
        </Empty>
      ) : (
        <MyExplorations
          data={explorations}
          onDelete={openDelete}
          onEdit={openEdit}
          onDuplicate={openDuplicate}
          archived={archived}
        />
      )}
      <CreateExplorationSheet
        open={open.create.open}
        onOpenChange={(next) =>
          setOpen((prev) => ({
            ...prev,
            create: {
              open: next,
              prefill: next ? prev.create.prefill : undefined,
            },
          }))
        }
        setExplorations={setExplorations}
        data={{
          rotations: explorations.rotations,
          sessions: explorations.sessions,
          teachers: explorations.teachers,
        }}
        prefill={open.create.prefill}
      />
      {open.delete.exploration && (
        <DeleteExplorationDialog
          open={open.delete.open}
          onOpenChange={(open) =>
            setOpen((prev) => ({
              ...prev,
              delete: { open, exploration: prev.delete.exploration },
            }))
          }
          exploration={open.delete.exploration}
          setExplorations={setExplorations}
        />
      )}
      {open.edit.exploration && (
        <EditExplorationSheet
          open={open.edit.open}
          onOpenChange={(open) =>
            setOpen((prev) => ({
              ...prev,
              edit: { open, exploration: prev.edit.exploration },
            }))
          }
          setExplorations={setExplorations}
          data={{
            rotations: explorations.rotations,
            sessions: explorations.sessions,
            teachers: explorations.teachers,
          }}
          exploration={open.edit.exploration}
        />
      )}
    </Dashboard>
  );
};

export default TeacherMyExplorations;
